<template>
  <div class="achievement-container">
    <a-row class="search-achievement">
      <a-col flex="130px">
        <a-radio-group v-model:value="mode" button-style="solid" @change="onChangeMode">
          <a-radio-button value="annual">{{ $t('annual') }}</a-radio-button>
          <a-radio-button value="monthly">{{ $t('monthly') }}</a-radio-button>
        </a-radio-group>
      </a-col>

      <a-col flex="260px">
        <a-select
          v-model:value="wasteType"
          class="major-category"
          @change="onChangeWasteType"
          :allowClear="false"
          :placeholder="$t('waste_type')"
        >
          <a-select-option :value="2">{{ $t('REGULAR') }}</a-select-option>
          <a-select-option :value="1">{{ $t('INDUSTRIAL') }}</a-select-option>
        </a-select>
      </a-col>

      <a-col flex="360px">
        <a-select
          show-search
          v-model:value="wasteCode"
          class="middle-category custom-select-input"
          @change="onChangeWasteCode"
          :allowClear="false"
          :placeholder="$t('waste_name')"
          :filter-option="wasteType === 1 ? filterWasteOption : undefined"
        >
          <template v-if="wasteType == 1" v-for="mdCategory in listWaste">
            <a-select-option :value="mdCategory.id">
              <span style="white-space: normal; word-wrap: break-word">{{ mdCategory.name }}</span>
            </a-select-option>
          </template>
          <template v-if="wasteType == 2">
            <a-select-option :value="WASTE.WASTE_REGULAR_TYPE.BURNABLE_WASTE">{{
              WASTE.WASTE_REGULAR_TYPE.BURNABLE_WASTE
            }}</a-select-option>
            <a-select-option :value="WASTE.WASTE_REGULAR_TYPE.GLASS_BOTTLES">{{
              WASTE.WASTE_REGULAR_TYPE.GLASS_BOTTLES
            }}</a-select-option>
            <a-select-option :value="WASTE.WASTE_REGULAR_TYPE.CARDBOARD">{{
              WASTE.WASTE_REGULAR_TYPE.CARDBOARD
            }}</a-select-option>
          </template>
        </a-select>
      </a-col>

      <a-col flex="auto" class="popup-button" v-if="authStore.isTransporter">
        <a-button
          type="primary"
          @click="openPopupReportWaste"
          :disabled="!creationStatus.regularAvailable"
          :class="!creationStatus.regularAvailable ? 'disabled' : ''"
        >
          {{ $t('report_complete_transporty_waste') }}
        </a-button>
        <a-button
          type="primary"
          @click="openPopupReportIndsWaste"
          :disabled="!creationStatus.industryAvailable"
          :class="!creationStatus.industryAvailable ? 'disabled' : ''"
        >
          {{ $t('report_complete_transport_industry_waste') }}
        </a-button>
      </a-col>
    </a-row>

    <a-row>
      <a-col class="achievement-list" :xs="24" :sm="24" :md="24" :lg="24">
        <a-table
          :columns="column"
          :dataSource="listAchievement"
          :pagination="false"
          :scroll="{ y: tableHeight }"
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'time'">
              <span>{{ generateTime(text) }}</span>
            </template>
            <template v-if="column.dataIndex === 'type'">
              <span>{{ wasteType == 1 ? $t('INDUSTRIAL') : $t('REGULAR') }}</span>
            </template>
            <template v-if="column.dataIndex === 'wasteName'">
              <div class="ant-table-cell-ellipsis">
                <a-tooltip :title="selectedWasteName">
                  <span>{{ selectedWasteName }}</span>
                </a-tooltip>
              </div>
            </template>
            <template v-if="column.dataIndex === 'companyName'">
              <div class="ant-table-cell-ellipsis">
                <a-tooltip :title="text">
                  {{ text }}
                </a-tooltip>
              </div>
            </template>
            <template v-if="column.dataIndex === 'volume'">
              <router-link :to="generateLinkDetail(record.time, record.companyId)" v-if="wasteType == 1">
                <span v-html="text"></span>
              </router-link>
              <span v-else>{{ AchievementService.convertWeight(text) + 'kg' }}</span>
            </template>
            <template v-if="column.dataIndex === 'status'">
              <span
                v-if="record.status != null && record.status != ACHIEVEMENT.STATUS.BLANK"
                class="status"
              >
                {{
                  $t(
                    record.status == ACHIEVEMENT.STATUS.ACCEPTANCE_OR_DENIAL ||
                      record.status == ACHIEVEMENT.STATUS.REQUEST_DELETE
                      ? authStore.isDischarger
                        ? 'awaiting_approval_2'
                        : 'awaiting_approval'
                      : 'rejection'
                  )
                }}
              </span>
              <span v-else></span>
            </template>
          </template>

          <template #headerCell="{ column }">
            <template v-if="column.dataIndex === 'time'">
              <span>{{ mode === 'monthly' ? $t('month') : $t('year') }}</span>
            </template>
          </template>
        </a-table>
      </a-col>
    </a-row>

    <a-button
      class="download-csv"
      @click="downloadCsv"
      :disabled="listAchievement == undefined || listAchievement.length == 0"
    >
      {{ $t('output_result_as_csv') }}
    </a-button>
  </div>

  <PopupReportTransportIndustryWaste
    v-model:visible="isPopupReportIndsWasteVisible"
    @close="closePopupReportIndsWaste"
  />
  <PopupReportTransportWaste
    v-model:visible="isPopupReportWasteVisible"
    @close="closePopupReportTransportWaste"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, watch, h } from 'vue';
import AchievementService from '@/services/modules/AchievementService';
import PopupReportTransportIndustryWaste from './PopupReportTransportIndustryWaste.vue';
import PopupReportTransportWaste from './PopupReportTransportWaste.vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore, useCommonStore, useAchievementStore, useUserStore } from '@/stores';
import { ACHIEVEMENT, WASTE } from '@/constants';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { customEllipsis } from '@/services/modules/home';
import { SearchOutlined } from '@ant-design/icons-vue';

const mode = ref('');
const wasteType: any = ref();
const wasteCode: any = ref();
const selectedWasteName = ref();
const listWaste: any = ref([]);
const listAchievement: any = ref();
const isPopupReportIndsWasteVisible = ref(false);
const isPopupReportWasteVisible = ref(false);
const { t } = useI18n();
const authStore = useAuthStore();
const achievementStore = useAchievementStore();
const userStore = useUserStore();
const tableHeight = ref('0px');
const route = useRoute();
const commonStore = useCommonStore();
const { selectedCompanyId } = storeToRefs(commonStore);
const creationStatus: any = ref({});

const centerHeader = () => {
  return {
    style: {
      textAlign: 'center'
    }
  };
};

const rightCell = () => {
  return {
    style: {
      textAlign: 'right'
    }
  };
};

const toHalfWidthChar = (char: string): string => {
  const fullwidthCharCode = char.charCodeAt(0);
  if (fullwidthCharCode >= 0xff01 && fullwidthCharCode <= 0xff5e) {
    return String.fromCharCode(fullwidthCharCode - 0xfee0);
  }
  return char;
};

const toHalfWidthString = (str: string): string => {
  return Array.from(str).map(toHalfWidthChar).join('');
};

const filterWasteOption = (input: string, option: { value: number; children?: any }): boolean => {
  const matchingCategory = listWaste.value.find((item: any) => item.id === option.value);
  const inputHalfWidth = toHalfWidthString(input.toLowerCase());
  const categoryNameHalfWidth = toHalfWidthString(matchingCategory?.name.toLowerCase());
  return categoryNameHalfWidth.includes(inputHalfWidth) || false;
};

const column = [
  { title: t('month'), dataIndex: 'time', key: 'time', width: 100, align: 'center' },
  { title: t('type'), dataIndex: 'type', key: 'type', width: 150, customHeaderCell: centerHeader },
  {
    title: t('waste_name'),
    dataIndex: 'wasteName',
    key: 'wasteName',
    width: 300,
    customHeaderCell: centerHeader,
    customCell: customEllipsis
  },
  {
    title: t('company_name'),
    dataIndex: 'companyName',
    key: 'companyName',
    width: 250,
    customHeaderCell: centerHeader,
    customCell: customEllipsis
  },
  {
    title: t('transport_volume'),
    dataIndex: 'volume',
    key: 'volume',
    width: 130,
    customCell: rightCell,
    customHeaderCell: centerHeader
  },
  { title: t('admit_reject'), dataIndex: 'status', key: 'status', width: 120, align: 'center' }
];

watch(route, (to) => {
  if (!to.path.startsWith('/achievements') && !to.path.startsWith('/achievements/detail')) {
    achievementStore.filter = undefined;
  }
});

onMounted(async () => {
  const inputSearchElement = document.querySelector('.custom-select-input input[type="search"]');
  if (inputSearchElement) {
    inputSearchElement.setAttribute('maxlength', '32');
  }
  window.addEventListener('resize', updateTableHeight);
  if (authStore.isTransporter) {
    creationStatus.value = await AchievementService.getCreationStatus(selectedCompanyId.value);
  }
  await fetchData(false);
  updateTableHeight();
});

const fetchData = async (needClearFilter: boolean) => {
  listWaste.value = await AchievementService.getListIndustryWaste();
  if (!needClearFilter && achievementStore.filter != undefined) {
    mode.value = achievementStore.filter.mode != null ? achievementStore.filter.mode : 'annual';
    wasteType.value =
      achievementStore.filter.wasteType != null ? achievementStore.filter.wasteType : 2;
    wasteCode.value =
      achievementStore.filter.wasteType != null && achievementStore.filter.wasteCode != null
        ? achievementStore.filter.wasteCode
        : WASTE.WASTE_REGULAR_TYPE.BURNABLE_WASTE;
  } else {
    achievementStore.filter = undefined;
    mode.value = 'annual';
    wasteType.value = 2;
    wasteCode.value = WASTE.WASTE_REGULAR_TYPE.BURNABLE_WASTE;
  }
  await searchAchievement();
  achievementStore.getFilter().wasteType = wasteType.value;
  achievementStore.getFilter().wasteCode = wasteCode.value;
  getSelectedWasteName();
};

watch([selectedCompanyId], async () => {
  if (authStore.isTransporter) {
    creationStatus.value = await AchievementService.getCreationStatus(selectedCompanyId.value);
  }
  fetchData(true);
});

const updateTableHeight = () => {
  const tableTop = document.querySelector('.ant-table-wrapper')?.getBoundingClientRect().top || 0;
  const csvBtnTop = document.querySelector('.download-csv')?.getBoundingClientRect().top || 0;
  tableHeight.value = `calc(${csvBtnTop}px - ${tableTop}px - 75px)`;
};

const onChangeMode = async () => {
  achievementStore.getFilter().mode = mode.value;
  await searchAchievement();
};

const onChangeWasteType = async (type: number | undefined) => {
  wasteCode.value = type == 1 ? listWaste.value[0]?.id : WASTE.WASTE_REGULAR_TYPE.BURNABLE_WASTE;
  await searchAchievement();
  achievementStore.getFilter().wasteType = wasteType.value;
  achievementStore.getFilter().wasteCode = wasteCode.value;
  getSelectedWasteName();
};

const onChangeWasteCode = async () => {
  await searchAchievement();
  achievementStore.getFilter().wasteCode = wasteCode.value;
  getSelectedWasteName();
};

const getSelectedWasteName = () => {
  if (wasteType.value == 1) {
    selectedWasteName.value = listWaste.value.find((waste: any) => waste.id == wasteCode.value)
      ?.name;
  } else {
    selectedWasteName.value = wasteCode.value;
  }
};

const searchAchievement = async () => {
  let wasteCompanyId = undefined;
  if (
    !authStore.isDischarger &&
    selectedCompanyId.value != null &&
    selectedCompanyId.value != 'ALL'
  ) {
    wasteCompanyId = Number(selectedCompanyId.value);
  }
  listAchievement.value = await AchievementService.getListAchievement(
    authStore.userRoles,
    mode.value,
    wasteType.value,
    wasteCode.value,
    wasteCompanyId
  );
};

const generateTime = (time: string) => {
  if (time == null) {
    return '';
  }
  if (time.toString().length == 4) {
    return time.toString();
  }
  return time.toString().substring(0, 4) + '/' + time.toString().substring(4, 6);
};

const generateLinkDetail = (time: string, companyId: number) => {
  let url = '/achievements/detail?year=';
  if (mode.value == 'annual') {
    url += time;
  } else {
    const year: string = time.toString().substring(0, 4);
    const month: string = time.toString().substring(4, 6);
    url += year + '&month=' + month;
  }
  url += '&wasteType=' + wasteType.value + '&wasteCode=' + wasteCode.value;
  if (companyId != null) {
    url += '&wasteCompanyId=' + companyId;
  }
  return url;
};

const downloadCsv = async () => {
  let data: any = [];
  listAchievement.value.forEach((element: any) => {
    let status =
      element.status != null && element.status != ACHIEVEMENT.STATUS.BLANK
        ? element.status == ACHIEVEMENT.STATUS.ACCEPTANCE_OR_DENIAL ||
          element.status == ACHIEVEMENT.STATUS.REQUEST_DELETE
          ? authStore.isDischarger
            ? t('awaiting_approval_2')
            : t('awaiting_approval')
          : t('rejection')
        : '';
    let obj = {
      [t('month')]: generateTime(element.time),
      [t('type')]: wasteType.value == 1 ? t('INDUSTRIAL') : t('REGULAR'),
      [t('waste_name')]: selectedWasteName.value,
      [t('company_name')]: element.companyName,
      [t('transport_volume')]: wasteType.value == 1 ? element.volume.replaceAll('<br/>', ' ') : AchievementService.convertWeight(element.volume) + 'kg',
      [t('admit_reject')]: status
    };
    data.push(obj);
  });
  AchievementService.downloadCsv(data, t('achievement_csv_file_name'), null);
};

const openPopupReportIndsWaste = () => {
  isPopupReportIndsWasteVisible.value = true;
};

const closePopupReportIndsWaste = async (needUpdate: boolean | null) => {
  if (needUpdate) {
    await searchAchievement();
    creationStatus.value = await AchievementService.getCreationStatus(selectedCompanyId.value);
  }
  isPopupReportIndsWasteVisible.value = false;
};

const openPopupReportWaste = () => {
  isPopupReportWasteVisible.value = true;
};

const closePopupReportTransportWaste = async (needUpdate: boolean | null) => {
  if (needUpdate) {
    await searchAchievement();
    creationStatus.value = await AchievementService.getCreationStatus(selectedCompanyId.value);
  }
  isPopupReportWasteVisible.value = false;
};
const onSearch = (value: string) => {
  if (value.length > 32) {
    value = value.slice(0, 32);
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.achievement-container {
  .search-achievement {
    width: 100%;

    > div {
      display: inline-block;
      padding-bottom: 1rem;
    }

    > div:nth-child(4) {
      button {
        background-color: #1677ff;
        color: #ffffff;
        margin-bottom: 5px;
      }

      button.disabled {
        background-color: #bfbfbf;
      }

      > button:nth-child(1) {
        margin-right: 10px;
      }

      button:hover {
        background-color: #1677ff;
      }

      button.disabled:hover {
        background-color: #bfbfbf;
      }
    }

    .major-category {
      width: 250px;
    }

    @media (max-width: 270px) {
      .major-category {
        width: 100%;
      }
    }

    .middle-category {
      width: 350px;
    }

    @media (max-width: 370px) {
      .middle-category {
        width: 100%;
      }
    }

    .popup-button {
      text-align: left;
    }

    @media (min-width: 1446px) {
      .popup-button {
        text-align: right;
      }
    }
  }

  span.status {
    width: 100px;
    padding-top: 5px;
    padding-bottom: 5px;
    border-radius: 5px;
    font-size: 12px;
    text-align: center;
    color: #ffffff;
    background-color: #f67e7e;
    display: inline-block;
  }

  .achievement-list {
    height: calc(70vh - 110px);
  }

  .achievement_footer {
    text-align: center;

    button {
      border: 2px solid #5b9bd5;
      width: 200px;
      height: 40px;
      color: #5b9bd5;
      font-weight: bold;
    }
  }

  .download-csv {
    border: 2px solid #5b9bd5;
    width: 200px;
    height: 40px;
    color: #5b9bd5;
    font-weight: bold;
    position: fixed;
    bottom: 50px;
    left: calc(280px + (100% - 280px) / 2 - 100px);
  }

  @media (max-width: 991px) {
    .download-csv {
      left: calc(100% / 2 - 100px);
    }
  }
}

:deep(.ant-table > .ant-table-container) {
  border: 1px solid gainsboro;
}

::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

:deep(.ant-table-body::-webkit-scrollbar) {
  height: 4px;
  width: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-track) {
  background-color: #e1e6e6;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb) {
  border-radius: 6px;
  background-color: gray;
}
:deep(.anticon-search) {
  font-size: 15px;
}
</style>
